import { getApiAddress } from '@/core/apiConfig';
import { UserRole } from '../../pages/UserPreferences/types';
import { DecoratorProfile } from '../mockups/mockupsDto';

const ORG_SETTINGS_V1 = 'org-settings/v1';

const parseResponse = (response: Response, parseJson = true) => {
  const { status } = response;

  if (status == 404) {
    // if it doesnt exist, return undefined to create new one
    return undefined;
  }

  if (status < 200 || status >= 400) {
    throw new Error(status.toString());
  }

  return parseJson ? response.json() : response;
};

export class OrgSettingsApi {
  /**
   * Get my orgId.
   */
  static async getMe(): Promise<{
    orgId: string;
    userId: string;
    role: string;
    isAdmin: boolean;
  }> {
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');

    try {
      const response = await fetch(`${getApiAddress()}/${ORG_SETTINGS_V1}/users/me`, {
        credentials: 'include',
        headers,
        method: 'GET',
      });

      const parsedResponse = await response.json();
      const isStyleAdmin = parsedResponse.find((user: any) => user?.role === 'is_seddi_style_admin')
        ? true
        : false;

      const isSeddiAdmin = parsedResponse.find((user: any) => user.role === 'is_seddi_admin')
        ? true
        : false;

      const isStylePublisher = parsedResponse.find(
        (user: any) => user.role === 'is_seddi_style_publisher',
      )
        ? true
        : false;

      const isAdmin = isStyleAdmin || isSeddiAdmin || isStylePublisher;

      const [{ orgId, userId, role }] = parsedResponse;
      return { orgId, userId, role, isAdmin };
    } catch (error) {
      console.error('Error fetching users org');
      throw error;
    }
  }

  /**
   * Get my orgId.
   */
  static async getMeFullResponse(): Promise<{ orgId: string; userId: string; role: string }[]> {
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');

    try {
      const response = await fetch(`${getApiAddress()}/${ORG_SETTINGS_V1}/users/me`, {
        credentials: 'include',
        headers,
        method: 'GET',
      });

      const parsedResponse = await response.json();

      return parsedResponse;
    } catch (error) {
      console.error('Error fetching users org');
      throw error;
    }
  }

  /**
   * Get my org
   */
  static async getOrg() {
    const { orgId } = await OrgSettingsApi.getMe();

    const headers = new Headers();
    headers.append('Content-Type', 'application/json');

    try {
      const response = await fetch(`${getApiAddress()}/${ORG_SETTINGS_V1}/org-settings/${orgId}`, {
        credentials: 'include',
        headers,
        method: 'GET',
      });

      return response.json();
    } catch (error) {
      console.error('Error fetching org info');
      throw error;
    }
  }

  /**
   * Get organization public info by ID.
   * @param id Org ID.
   */
  static async getOrgPublic(id: string) {
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');

    try {
      const response = await fetch(
        `${getApiAddress()}/${ORG_SETTINGS_V1}/org-settings/public/${id}`,
        {
          credentials: 'include',
          headers,
          method: 'GET',
        },
      );
      return parseResponse(response);
    } catch (error) {
      if (!error.toString().includes('Failed to fetch')) {
        console.error(error);
      }
    }
  }

  /**
   * Get my role
   * @returns
   */
  static async getMyRole(orgId?: string) {
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');

    const response = fetch(`${getApiAddress()}/${ORG_SETTINGS_V1}/users/me`, {
      credentials: 'include',
      headers,
      method: 'GET',
    })
      .then(response => {
        return response.json().then(response => {
          if (response.length === 1) {
            return { role: response[0].role, seddiAdmin: false, myId: response[0].userId };
          } else {
            return {
              role: response.find((item: any) => item.orgId === orgId)?.role,
              seddiAdmin:
                response.find((item: any) => item.orgId === 'main')?.role === 'is_seddi_admin',
              myId: response[0].userId,
            };
          }
        });
      })
      .catch(err => {
        if (!err.toString().includes('Failed to fetch')) {
          console.error(err);
        }
      });

    return response;
  }

  static async updateUserRole(userId: string, orgId: string, role: UserRole) {
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');

    try {
      await fetch(`${getApiAddress()}/${ORG_SETTINGS_V1}/users/${userId}`, {
        credentials: 'include',
        headers,
        method: 'PUT',
        body: JSON.stringify({
          orgId,
          role,
        }),
      });
    } catch (error) {
      console.error('Error updating role');
      throw error;
    }
  }

  static async getUsers(orgId: string) {
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');

    const response = await fetch(
      `${getApiAddress()}/${ORG_SETTINGS_V1}/org-settings/${orgId}/users`,
      {
        credentials: 'include',
        headers,
        method: 'GET',
      },
    )
      .then(response => {
        return response.json();
      })
      .catch(error => {
        console.error('Error fetching users');
        throw error;
      });

    return response;
  }

  static async getSeatLimits(
    orgId: string,
    scope: string = 'DECORATOR_SEATS',
    name: string = 'editor',
  ) {
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');

    try {
      const response = await fetch(
        `${getApiAddress()}/${ORG_SETTINGS_V1}/limits/${orgId}/scope/${scope}/name/${name}`,
        {
          credentials: 'include',
          headers,
          method: 'GET',
        },
      );

      return parseResponse(response);
    } catch (error) {
      console.error('Error fetching seat limits');
      throw error;
    }
  }

  static async updateOrgName(orgId: string, name: string): Promise<void> {
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');

    try {
      await fetch(`${getApiAddress()}/${ORG_SETTINGS_V1}/org-settings/${orgId}`, {
        credentials: 'include',
        headers,
        method: 'PATCH',
        body: JSON.stringify({
          name,
        }),
      });
    } catch (error) {
      console.error('Error updating org name');
      throw error;
    }
  }

  static async updateOrgTechpackDisclaimer(
    orgId: string,
    techpackDisclaimer: string,
  ): Promise<void> {
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');

    try {
      await fetch(`${getApiAddress()}/${ORG_SETTINGS_V1}/org-settings/${orgId}`, {
        credentials: 'include',
        headers,
        method: 'PATCH',
        body: JSON.stringify({
          techpackDisclaimer,
        }),
      });
    } catch (error) {
      console.error('Error updating org techpack disclaimer');
      throw error;
    }
  }

  static async getStripeSession(orgId: string) {
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');

    const response = await fetch(
      `${getApiAddress()}/${ORG_SETTINGS_V1}/org-settings/${orgId}/stripe-session`,
      {
        credentials: 'include',
        headers,
        method: 'GET',
      },
    );

    return parseResponse(response);
  }

  static async getLogoUploadUrl(orgId: string) {
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');

    const response = await fetch(
      `${getApiAddress()}/${ORG_SETTINGS_V1}/org-settings/${orgId}/logo-upload-url`,
      {
        credentials: 'include',
        headers,
        method: 'GET',
      },
    );

    return parseResponse(response);
  }

  static async updateOrgLogo(orgId: string, path: string) {
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');

    const response = await fetch(
      `${getApiAddress()}/${ORG_SETTINGS_V1}/org-settings/${orgId}/update-logo`,
      {
        credentials: 'include',
        headers,
        method: 'PUT',
        body: JSON.stringify({ path }),
      },
    );

    return parseResponse(response);
  }

  static async deleteOrgLogo(orgId: string) {
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');

    const response = await fetch(
      `${getApiAddress()}/${ORG_SETTINGS_V1}/org-settings/${orgId}/delete-logo`,
      {
        credentials: 'include',
        headers,
        method: 'DELETE',
      },
    );

    return response.status === 204;
  }

  static async updateOrgProductNamingConvention(
    orgId: string,
    productNamingConvention: string,
  ): Promise<void> {
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');

    try {
      await fetch(`${getApiAddress()}/${ORG_SETTINGS_V1}/org-settings/${orgId}`, {
        credentials: 'include',
        headers,
        method: 'PATCH',
        body: JSON.stringify({
          productNamingConvention,
        }),
      });
    } catch (error) {
      console.error('Error updating org product naming convention');
      throw error;
    }
  }

  static async updateOrgDecoratorProfile(
    orgId: string,
    decoratorProfile: DecoratorProfile | undefined,
  ): Promise<void> {
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');

    try {
      await fetch(`${getApiAddress()}/${ORG_SETTINGS_V1}/org-settings/${orgId}`, {
        credentials: 'include',
        headers,
        method: 'PATCH',
        body: JSON.stringify({
          decoratorProfile,
        }),
      });
    } catch (error) {
      console.error('Error updating org decorator profile');
      throw error;
    }
  }
}
